/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: FirewallRulesPage.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Firewall Rules page - Lists and manages firewall rules
────────────────────────────────────────────────────────────────*/

import { useState, useCallback } from 'react';
import { Shield, Play } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { ModulePageContainer } from '../../components/layouts';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useFirewallRulesList } from './hooks/useFirewallRulesList';
import { FirewallRuleModal } from './components/FirewallRuleModal';
import { deleteFirewallRule, applyFirewallRules } from './api/firewallRulesApi';
import { Edit, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import type { FirewallRule } from './types';

export default function FirewallRulesPage() {
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<FirewallRule | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    rule: FirewallRule | null;
  }>({
    isOpen: false,
    rule: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  const {
    rules,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useFirewallRulesList();

  const handleAdd = useCallback(() => {
    setEditingRule(null);
    setModalOpen(true);
  }, []);

  const handleEdit = useCallback((rule: FirewallRule) => {
    setEditingRule(rule);
    setModalOpen(true);
  }, []);

  const handleDelete = useCallback((rule: FirewallRule) => {
    setDeleteModal({ isOpen: true, rule });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.rule) return;

    setIsDeleting(true);
    try {
      await deleteFirewallRule(deleteModal.rule.id);
      showSuccess(t('firewallRules.messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, rule: null });
      refresh();
      
      // Apply changes to the system firewall
      try {
        await applyFirewallRules();
        showSuccess(t('firewallRules.messages.appliedToFirewall'));
      } catch (err) {
        // Show warning but don't fail the delete operation
        showError(t('firewallRules.messages.applyError') + ': ' + (err instanceof Error ? err.message : ''));
      }
    } catch (error) {
      showError(error instanceof Error ? error.message : t('firewallRules.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.rule, refresh, showSuccess, showError, t]);

  const handleApplyRules = useCallback(async () => {
    setIsApplying(true);
    try {
      await applyFirewallRules();
      showSuccess(t('firewallRules.messages.appliedToFirewall'));
    } catch (error) {
      showError(t('firewallRules.messages.applyError') + ': ' + (error instanceof Error ? error.message : ''));
    } finally {
      setIsApplying(false);
    }
  }, [showSuccess, showError, t]);

  const handleModalSuccess = useCallback(() => {
    refresh();
  }, [refresh]);

  const renderActions = useCallback(
    (rule: FirewallRule) => (
      <div className="flex items-center gap-2">
        <SimpleTooltip content={t('common.edit')} position="top">
          <button
            onClick={() => handleEdit(rule)}
            className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </button>
        </SimpleTooltip>

        <SimpleTooltip content={t('common.delete')} position="top">
          <button
            onClick={() => handleDelete(rule)}
            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </div>
    ),
    [handleEdit, handleDelete, t]
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          title={t('firewallRules.title')}
          titleIcon={<Shield className="h-5 w-5 text-primary" />}
          data={rules}
          columns={columns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          onSearch={handleSearch}
          onColumnToggle={handleColumnToggle}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          emptyMessage={t('firewallRules.noRulesFound')}
          searchPlaceholder={t('firewallRules.searchPlaceholder')}
          renderActions={renderActions}
          addButtonText={t('common.add')}
          onAdd={handleAdd}
          actions={
            <SimpleTooltip content={t('firewallRules.applyRules')} position="top">
              <Button
                onClick={handleApplyRules}
                disabled={isApplying || loading}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <Play className={`h-4 w-4 ${isApplying ? 'animate-spin' : ''}`} />
                {isApplying ? t('common.applying') : t('firewallRules.apply')}
              </Button>
            </SimpleTooltip>
          }
          translations={{
            items: t('common.items'),
            columns: t('common.columns'),
            noItemsFound: t('common.noItemsFound'),
            showing: t('common.showing'),
            to: t('common.to'),
            of: t('common.of'),
            results: t('common.results'),
            previous: t('common.previous'),
            next: t('common.next'),
            page: t('common.page'),
            deleteItem: t('common.deleteItem'),
            areYouSureDeleteItem: t('common.areYouSureDeleteItem'),
            delete: t('common.delete'),
            cancel: t('common.cancel'),
            allSelected: t('common.allSelected'),
            selected: t('common.selected'),
            searchOptions: t('common.searchOptions'),
            noOptionsFound: t('common.noOptionsFound'),
            selectAll: t('common.selectAll'),
            clearAll: t('common.clearAll'),
          }}
        />
      }
      formView={null}
    >
      <FirewallRuleModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingRule(null);
        }}
        onSuccess={handleModalSuccess}
        rule={editingRule}
      />

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, rule: null })}
        onConfirm={handleDeleteConfirm}
        title={t('firewallRules.deleteModal.title')}
        message={t('firewallRules.deleteModal.message', {
          name: deleteModal.rule?.name || '',
        })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}
